// v10/src/components/admin/EmergencyStats.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, Award, AlertTriangle, User, Loader, ChevronRight } from 'lucide-react';
import { emergencyService } from '../../services/apiService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const EmergencyStats = ({ darkMode, currentUser, onClose, inline }) => {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPilot, setSelectedPilot] = useState(null);
  const [pilotStats, setPilotStats] = useState(null);
  const [pilotLoading, setPilotLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await emergencyService.overviewStats();
        setOverview(res.data?.data || []);
      } catch (err) {
        console.error('[EmergencyStats] load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadPilotStats = useCallback(async (pilotId) => {
    setPilotLoading(true);
    try {
      const res = await emergencyService.pilotStats(pilotId);
      setPilotStats(res.data?.data || null);
    } catch (err) {
      console.error('[EmergencyStats] pilotStats error:', err);
    } finally {
      setPilotLoading(false);
    }
  }, []);

  const selectPilot = (pilot) => {
    setSelectedPilot(pilot);
    loadPilotStats(pilot.pilot_id);
  };

  const getScoreColor = (s) => s >= 80 ? '#22c55e' : s >= 60 ? '#f59e0b' : '#ef4444';

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  const content = (
    <>
        <div style={{ height:4, background:'linear-gradient(90deg,#ef4444,#f97316,#f59e0b)', borderRadius: onClose ? 0 : '4px 4px 0 0' }} />

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h2 style={{ margin:0, fontSize:18, fontWeight:800, color: darkMode ? 'white' : '#0f172a', display:'flex', alignItems:'center', gap:8 }}>
              <span>🚨</span> Emergency Trainer — Estadísticas
            </h2>
          </div>
          {onClose && (
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', padding:4 }}>
              <X size={22} color={darkMode ? 'rgba(255,255,255,0.6)' : '#94a3b8'} />
            </button>
          )}
        </div>

        <div style={{ padding:'16px 24px 24px' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:40 }}>
              <Loader size={24} className="animate-spin" color="#818cf8" />
            </div>
          ) : overview.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 20px' }}>
              <div style={{ fontSize:48, marginBottom:12 }}>📊</div>
              <p style={{ fontSize:15, fontWeight:600, color: darkMode ? 'rgba(255,255,255,0.6)' : '#64748b' }}>
                Aún no hay sesiones completadas
              </p>
            </div>
          ) : selectedPilot ? (
            /* ── Pilot detail view ── */
            <div>
              <button onClick={() => { setSelectedPilot(null); setPilotStats(null); }}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:10, border:`1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, background:'transparent', color: darkMode ? 'rgba(255,255,255,0.7)' : '#475569', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', marginBottom:16 }}>
                ← Volver a la lista
              </button>

              <h3 style={{ margin:'0 0 16px', fontSize:16, fontWeight:800, color: darkMode ? 'white' : '#0f172a' }}>
                {isAdmin ? selectedPilot.pilot_name : `Piloto #${overview.indexOf(selectedPilot) + 1}`}
              </h3>

              {pilotLoading ? (
                <div style={{ textAlign:'center', padding:30 }}><Loader size={20} className="animate-spin" color="#818cf8" /></div>
              ) : pilotStats ? (
                <>
                  {/* Stats summary */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:20 }}>
                    {[
                      { icon: <Award size={18} color="#22c55e" />, value: pilotStats.best_score, label: 'Mejor score' },
                      { icon: <TrendingUp size={18} color="#818cf8" />, value: pilotStats.avg_score, label: 'Promedio' },
                      { icon: <AlertTriangle size={18} color="#f59e0b" />, value: pilotStats.total_sessions, label: 'Sesiones' },
                    ].map((s, i) => (
                      <div key={i} style={{ padding:'14px 10px', borderRadius:14, background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc', border:`1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`, textAlign:'center' }}>
                        <div style={{ marginBottom:6 }}>{s.icon}</div>
                        <div style={{ fontSize:22, fontWeight:800, color: darkMode ? 'white' : '#0f172a' }}>{s.value}</div>
                        <div style={{ fontSize:11, color: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8', marginTop:2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Monthly chart */}
                  {pilotStats.monthly && pilotStats.monthly.length > 0 && (
                    <div style={{ marginBottom:20 }}>
                      <h4 style={{ margin:'0 0 12px', fontSize:13, fontWeight:700, color: darkMode ? 'rgba(255,255,255,0.6)' : '#475569' }}>
                        Evolución mensual
                      </h4>
                      <div style={{ height:200 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={pilotStats.monthly}>
                            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9'} />
                            <XAxis dataKey="month" tick={{ fontSize:11, fill: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize:11, fill: darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8' }} />
                            <Tooltip
                              contentStyle={{ background: darkMode ? '#1e293b' : 'white', border:'none', borderRadius:12, fontSize:12, boxShadow:'0 4px 20px rgba(0,0,0,0.15)' }}
                              formatter={(v, n) => [v, n === 'avg_score' ? 'Score promedio' : 'Sesiones']}
                            />
                            <Bar dataKey="avg_score" fill="#818cf8" radius={[6,6,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Worst procedure */}
                  {pilotStats.worst_procedure && (
                    <div style={{ padding:'12px 16px', borderRadius:14, background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.12)' }}>
                      <span style={{ fontSize:12, fontWeight:700, color:'#ef4444' }}>
                        Procedimiento con más errores: {pilotStats.worst_procedure.title} ({pilotStats.worst_procedure.total_errors} errores de orden)
                      </span>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          ) : (
            /* ── Pilots list ── */
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {overview.map((pilot, idx) => (
                <button key={pilot.pilot_id} onClick={() => selectPilot(pilot)}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', borderRadius:14, border:`1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`, background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc', cursor:'pointer', fontFamily:'inherit', textAlign:'left', width:'100%' }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#818cf8,#6d28d9)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <User size={18} color="white" />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color: darkMode ? 'white' : '#0f172a' }}>
                      {isAdmin ? pilot.pilot_name : `Piloto #${idx + 1}`}
                    </div>
                    <div style={{ fontSize:12, color: darkMode ? 'rgba(255,255,255,0.45)' : '#94a3b8', marginTop:2 }}>
                      {pilot.total_sessions} sesiones · Última: {pilot.last_session ? new Date(pilot.last_session).toLocaleDateString('es-AR') : '-'}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', marginRight:8 }}>
                    <div style={{ fontSize:20, fontWeight:800, color: getScoreColor(pilot.avg_score) }}>{pilot.avg_score}</div>
                    <div style={{ fontSize:10, color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>promedio</div>
                  </div>
                  <ChevronRight size={16} color="#94a3b8" />
                </button>
              ))}
            </div>
          )}
        </div>
    </>
  );

  // Modal mode (when called with onClose)
  if (onClose) {
    return (
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        style={{ position:'fixed', inset:0, zIndex:100, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <motion.div initial={{ scale:0.94, y:20 }} animate={{ scale:1, y:0 }}
          style={{ width:'100%', maxWidth:640, maxHeight:'90vh', overflow:'auto', borderRadius:24, background: darkMode ? '#0f172a' : 'white', boxShadow:'0 24px 60px rgba(0,0,0,0.25)' }}>
          {content}
        </motion.div>
      </motion.div>
    );
  }

  // Inline mode (AdminPanel tab)
  return (
    <div style={{ borderRadius:20, background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc', border:`1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`, overflow:'hidden' }}>
      {content}
    </div>
  );
};

export default EmergencyStats;
